import { format } from 'date-fns'
import { ReactNode, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Button } from '../../components/button'
import { BackButton } from '../../components/button/backButton'
import { Status } from '../../components/cards/statusTag'
import { ConfirmRefundActionModal } from '../../components/modal/app_modals/confirmRefundActionModal'
import Spinner from '../../components/spinner/Spinner'
import { PageHeader } from '../../components/typography/PageHeader'
import { RefundStatusMap } from '../../constants'
import { formatCurrency } from '../../helpers'
import { useModalControl } from '../../hooks/useModalControl'
import Layout from '../../layout'
import { useGetOrderRefundQuery } from '../../redux-toolkits/transactions/transactions.slice'

export const SingleOrderRefundPage = () => {
  const [refundAction, setRefundAction] = useState<'reject' | 'approve'>(
    'approve',
  )
  const [details, setDetails] = useState<
    ({ title: string; value: ReactNode | string } | undefined)[]
  >([])

  const { orderId } = useParams<{
    orderId: string
  }>()

  const { modal, modalOpen, setModalOpen, handleModalClose, handleModalOpen } =
    useModalControl()

  const { data: orderRefundDetails, isFetching: fetchingOrderRefundDetails } =
    useGetOrderRefundQuery(
      {
        id: orderId!,
      },
      {
        refetchOnMountOrArgChange: true,
        skip: !orderId,
      },
    )

  useEffect(() => {
    setDetails([
      {
        title: 'Customer Name:',
        value: (
          <Link
            to={`/customers/${orderRefundDetails?.data?.refund?.user?._id}`}
            className="hover:underline cursor-pointer !capitalize"
          >
            {orderRefundDetails?.data?.refund?.user?.firstname}{' '}
            {orderRefundDetails?.data?.refund?.user?.lastname}
          </Link>
        ),
      },
      {
        title: 'Order ID:',
        value: (
          <Link
            to={`/orders/${orderRefundDetails?.data?.refund?.order?.harmonizedReference}`}
            className="hover:underline cursor-pointer"
          >
            {orderRefundDetails?.data?.refund?.order?.orderNumber}
          </Link>
        ),
      },
      {
        title: 'Item for refund:',
        value: (
          <div className="flex flex-col gap-2">
            {orderRefundDetails?.data?.refund?.affectedItems?.map(
              (item, idx) => {
                return (
                  <Link to={''} key={`${idx}`}>
                    {item}
                  </Link>
                )
              },
            )}
          </div>
        ),
      },
      {
        title: 'Proof of refund:',
        value: (
          <div className="flex flex-wrap gap-2">
            {orderRefundDetails?.data?.refund?.proof?.map((url, idx) => {
              return (
                <img
                  key={`${idx}-proof`}
                  src={url}
                  className="cursor-pointer border block w-[140px] h-[140px] rounded-md object-cover"
                  onClick={() => {
                    window.open(url, '_blank')
                  }}
                />
              )
            })}
          </div>
        ),
      },
      {
        title: 'Refund type:',
        value:
          orderRefundDetails?.data?.refund?.type === 'PARTIAL'
            ? 'Partial refund'
            : 'Full refund',
      },
      {
        title: 'Date:',
        value: orderRefundDetails?.data?.refund?.createdAt
          ? format(
              orderRefundDetails?.data?.refund?.createdAt ?? '',
              'dd/MM/yyyy h:mma',
            )
          : '',
      },
      {
        title: 'Refund status:',
        value: (
          <Status
            type={
              RefundStatusMap[
                orderRefundDetails?.data?.refund?.status ?? 'PENDING'
              ].pillVariant
            }
            text={
              RefundStatusMap[
                orderRefundDetails?.data?.refund?.status ?? 'PENDING'
              ].name
            }
          />
        ),
      },
      {
        title: 'Amount to be refunded:',
        value: formatCurrency(
          orderRefundDetails?.data?.refund?.requestedRefundAmount ?? 0,
        ),
      },
      {
        title: 'Refund reason:',
        value: orderRefundDetails?.data?.refund?.refundReason ?? 'N/A',
      },
      {
        title: 'Initiated by:',
        value: `${orderRefundDetails?.data?.refund?.requestAdmin?.firstname} ${orderRefundDetails?.data?.refund?.requestAdmin?.lastname}`,
      },
      orderRefundDetails?.data?.refund?.status !== 'PENDING'
        ? {
            title: 'Completed by:',
            value: `${orderRefundDetails?.data?.refund?.finalStatusAdmin?.firstname} ${orderRefundDetails?.data?.refund?.finalStatusAdmin?.lastname}`,
          }
        : undefined,
      orderRefundDetails?.data?.refund?.status === 'REJECTED'
        ? {
            title: 'Refund rejection reason:',
            value: `${
              orderRefundDetails?.data?.refund?.reason ??
              orderRefundDetails?.data?.refund?.rejectionReason
            }`,
          }
        : undefined,
    ])
  }, [orderRefundDetails])

  return (
    <Layout>
      <div className="flex flex-col gap-10">
        {fetchingOrderRefundDetails ? (
          <Spinner fullScreen={false} />
        ) : (
          <>
            <BackButton />

            <div className="flex items-center justify-between gap-4">
              <PageHeader
                title={`Refund for Order #${orderRefundDetails?.data?.refund?.order?.orderNumber}`}
                className="!mb-0"
              />

              {orderRefundDetails?.data?.refund?.status === 'PENDING' && (
                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    label="Approve"
                    primary
                    className="!w-full !px-10"
                    onClick={() => {
                      handleModalOpen('refundActionmodal')
                      setRefundAction('approve')
                    }}
                  />
                  <Button
                    type="button"
                    label="Decline"
                    primary={false}
                    className="!w-full !px-10"
                    onClick={() => {
                      handleModalOpen('refundActionmodal')
                      setRefundAction('reject')
                    }}
                  />
                </div>
              )}
            </div>

            <div className="flex flex-col w-full max-w-[800px]">
              {details.map((detail, idx) => {
                if (detail) {
                  const key = `${detail.title}-${idx}`

                  return (
                    <div
                      className="
                  py-4
                  first:pt-0 last:pb-0
                  border-b last:border-b-0
                  flex gap-4
                  items-center justify-between
                "
                      key={key}
                    >
                      <span className="text-sec-black">{detail.title}</span>
                      <span className="text-black font-medium">
                        {detail.value}
                      </span>
                    </div>
                  )
                }
              })}
            </div>
          </>
        )}

        {orderRefundDetails?.data?.refund?._id && (
          <ConfirmRefundActionModal
            open={modal === 'refundActionmodal' && modalOpen}
            setOpen={setModalOpen}
            onCloseCallback={() => {
              handleModalClose()
            }}
            onSuccessCallback={() => {
              handleModalClose()
            }}
            refundId={orderRefundDetails?.data?.refund?._id}
            actionType={refundAction}
          />
        )}
      </div>
    </Layout>
  )
}
